import { useEffect, useState } from "react";
import {
    AnalyticsRankingMetric,
    AverageViewsPerStreamResult,
  MinutesViewedOverTimeResult,
  RankingsResult,
  StreamedMinutesOverTimeResult,
  StreamsOverTimeResult,
  TotalUserSignupsCount,
} from "../../../types/Analytics";
import useAnalyticsService from "../../../services/AnalyticsService";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import VideoGameAssetIcon from "@mui/icons-material/VideogameAsset";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import Ghost from "../../../components/Ghost";
import UserNameById from "../../../components/User/UserNameById";

interface LinkCardProps {
  to: string;
  title: string;
  description: string;
  icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
}

function LinkCard({ to, title, description, icon: Icon }: LinkCardProps) {
  return (
    <NavLink to={to} className="card p-4 flex flex-row gap-4">
      <div className="flex items-center h-full">
        <Icon />
      </div>
      <div className="flex flex-col gap-2 grow">
        <h2 className="text-lg xl:text-2xl">{title}</h2>
        <p>{description}</p>
      </div>
      <div className="flex items-center h-full">
        <ArrowForwardTwoToneIcon />
      </div>
    </NavLink>
  );
}

const formatNumber = (n: number, decimals = 0) => n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

export default function AdminAnalyticsOverviewPage() {
  const {
    getTotalUserSignups,
    getStreamingSummaryStats,
    getLiveAppRankings,
  } = useAnalyticsService();

  const navigate = useNavigate();

  const [userStatsRetrieved, setUserStatsRetrieved] = useState(false);
  const [userTotalSignups, setTotalSignups] = useState<TotalUserSignupsCount | null>(null);
  const [sevenDaysUserSignups, setSevenDaysUserSignups] = useState<TotalUserSignupsCount | null>(null);
  const [thirtyDaysUserSignups, setThirtyDaysUserSignups] = useState<TotalUserSignupsCount | null>(null);

  const [liveAppStreamCountsRetrieved, setLiveAppStreamCountsRetrieved] = useState(false);
  const [liveAppStreamCountsTotal, setLiveAppStreamCountsTotal] = useState<number | null>(null);
  const [liveAppStreamCountsSevenDays, setLiveAppStreamCountsSevenDays] = useState<number | null>(null);
  const [liveAppStreamCountsThirtyDays, setLiveAppStreamCountsThirtyDays] = useState<number | null>(null);
  const [liveAppStreamedMinutesTotal, setLiveAppStreamedMinutesTotal] = useState<number | null>(null);
  const [liveAppStreamedMinutesSevenDays, setLiveAppStreamedMinutesSevenDays] = useState<number | null>(null);
  const [liveAppStreamedMinutesThirtyDays, setLiveAppStreamedMinutesThirtyDays, ] = useState<number | null>(null);
  const [liveAppViewedMinutesTotal, setLiveAppViewedMinutesTotal] = useState<number | null>(null);
  const [liveAppViewedMinutesSevenDays, setLiveAppViewedMinutesSevenDays] = useState<number | null>(null);
  const [liveAppViewedMinutesThirtyDays, setLiveAppViewedMinutesThirtyDays] = useState<number | null>(null);

  const [rankingsRetrieved, setRankingsRetrieved] = useState(false);
  const [liveAppRankings, setLiveAppRankings] = useState<RankingsResult | null>(null);
  const [liveUserRankings, setLiveUserRankings] = useState<RankingsResult | null>(null);

  var midnightToday = new Date();
  midnightToday.setHours(0, 0, 0, 0);

  var sevenDaysAgo = new Date(midnightToday);
  sevenDaysAgo.setDate(midnightToday.getDate() - 7);

  var thirtyDaysAgo = new Date(midnightToday);
  thirtyDaysAgo.setDate(midnightToday.getDate() - 30);

  // Fetch rankings.
  useEffect(() => {
    if (rankingsRetrieved) return;
    setRankingsRetrieved(true);

    const fetchLiveAppRankings = async () => {
      try {

        const [appRankings, userRankings] = await Promise.all([
          getLiveAppRankings(sevenDaysAgo, midnightToday, AnalyticsRankingMetric.MinutesStreamed),
          getLiveAppRankings(sevenDaysAgo, midnightToday, AnalyticsRankingMetric.MinutesStreamed, false)
        ]);

        setLiveAppRankings(appRankings);
        setLiveUserRankings(userRankings);
      } catch (error) {
        console.error("Error fetching live app rankings:", error);
      }
    };

    fetchLiveAppRankings();
  }, [rankingsRetrieved]);

  // Fetch user signups data when the component mounts
  useEffect(() => {
    if (userStatsRetrieved) return;

    setUserStatsRetrieved(true);

    const fetchUserSignups = async () => {
      try {

        const [totalSignups, sevenDaysSignups, thirtyDaysSignups] = await Promise.all([
          getTotalUserSignups(new Date("2023-01-01"), new Date()),
          getTotalUserSignups(sevenDaysAgo, midnightToday),
          getTotalUserSignups(thirtyDaysAgo, midnightToday)
        ]);
        setTotalSignups(totalSignups);
        setSevenDaysUserSignups(sevenDaysSignups);
        setThirtyDaysUserSignups(thirtyDaysSignups);
      } catch (error) {
        console.error("Error fetching user signups:", error);
      }
    };

    fetchUserSignups();
  }, [userStatsRetrieved]);

  useEffect(() => {
    if (liveAppStreamCountsRetrieved) return;
    setLiveAppStreamCountsRetrieved(true);

    const fetchLiveAppStreamCounts = async () => {
      try {

        getStreamingSummaryStats().then(stats => {
          setLiveAppStreamCountsTotal(stats.streamCountAllTime);
          setLiveAppStreamCountsSevenDays(stats.streamCountLast7Days);
          setLiveAppStreamCountsThirtyDays(stats.streamCountLast30Days);
          setLiveAppStreamedMinutesTotal(stats.streamMinutesAllTime);
          setLiveAppStreamedMinutesSevenDays(stats.streamMinutesLast7Days);
          setLiveAppStreamedMinutesThirtyDays(stats.streamMinutesLast30Days);
          setLiveAppViewedMinutesTotal(stats.watchMinutesAllTime);
          setLiveAppViewedMinutesSevenDays(stats.watchMinutesLast7Days);
          setLiveAppViewedMinutesThirtyDays(stats.watchMinutesLast30Days);
          setLiveAppStreamCountsRetrieved(true);
        })
        .catch(error => {
          console.error("Error fetching streaming summary stats:", error);
        });
      
        
      } catch (error) {
        console.error("Error fetching live app stream counts:", error);
      }
    };

    fetchLiveAppStreamCounts();
  }, [liveAppStreamCountsRetrieved]);

  return (
    <>
      <section className="grid grid-cols gap-4 sm:hidden mb-4 sm:mb-0">
        <LinkCard
          to="/analytics/streams"
          title="Streams"
          description="Stream analytics"
          icon={LiveTvIcon}
        />
        <LinkCard
          to="/analytics/users"
          title="Users"
          description="User analytics"
          icon={PeopleAltIcon}
        />
        <LinkCard
          to="/analytics/titles"
          title="Titles"
          description="Title analytics"
          icon={VideoGameAssetIcon}
        />
      </section>
      <section className="grid grid-cols-1 gap-4">
        <h2 className="text-2xl">
          <Link
            title="User analytics"
            to="/analytics/users"
            className="underline bold"
          >
            Users <span className="text-sm">(view more)</span>
          </Link>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card p-4">
            <h3 className="text-xl pb-2">All time</h3>
            {userTotalSignups ? (
              <p>{userTotalSignups.count}</p>
            ) : (
              <p>Loading...</p>
            )}
          </div>
          <div className="card p-4">
            <h3 className="text-xl pb-2">Last 7 days</h3>
            {sevenDaysUserSignups ? (
              <p>{sevenDaysUserSignups.count}</p>
            ) : (
              <p>Loading...</p>
            )}
          </div>
          <div className="card p-4">
            <h3 className="text-xl pb-2">Last 30 days</h3>
            {thirtyDaysUserSignups ? (
              <p>{thirtyDaysUserSignups.count}</p>
            ) : (
              <p>Loading...</p>
            )}
          </div>
        </div>
      </section>
      <section className="grid grid-cols-1 gap-4 pt-4">
        <h2 className="text-2xl">
          <Link
            title="Stream analytics"
            to="/analytics/streams"
            className="underline bold"
          >
            Streams <span className="text-sm">(view more)</span>
          </Link>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 2xl:grid-cols-9 gap-4">
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Stream count (all time)</h3>
            {liveAppStreamCountsRetrieved ? (
              <p className="text-2xl md:text-4xl">
                {formatNumber(liveAppStreamCountsTotal ?? 0)}
              </p>
            ) : (
              <p className="text-2xl md:text-4xl">Loading...</p>
            )}
          </div>
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Stream count (7 days)</h3>
            {liveAppStreamCountsRetrieved ? (
              <p className="text-2xl md:text-4xl">
                {formatNumber(liveAppStreamCountsSevenDays ?? 0)}
              </p>
            ) : (
              <p className="text-2xl md:text-4xl">Loading...</p>
            )}
          </div>
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Stream count (30 days)</h3>
            {liveAppStreamCountsRetrieved ? (
              <p className="text-2xl md:text-4xl">
                {formatNumber(liveAppStreamCountsThirtyDays ?? 0)}
              </p>
            ) : (
              <p className="text-2xl md:text-4xl">Loading...</p>
            )}
          </div>
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Stream minutes (all time)</h3>
            {liveAppStreamCountsRetrieved ? (
              <p className="text-2xl md:text-4xl">
                {formatNumber(liveAppStreamedMinutesTotal ?? 0)}
              </p>
            ) : (
              <p className="text-2xl md:text-4xl">Loading...</p>
            )}
          </div>
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Stream minutes (7 days)</h3>
            {liveAppStreamCountsRetrieved ? (
              <p className="text-2xl md:text-4xl">
                {formatNumber(liveAppStreamedMinutesSevenDays ?? 0)}
              </p>
            ) : (
              <p className="text-2xl md:text-4xl">Loading...</p>
            )}
          </div>
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Stream minutes (30 days)</h3>
            {liveAppStreamCountsRetrieved ? (
              <p className="text-2xl md:text-4xl">
                {formatNumber(liveAppStreamedMinutesThirtyDays ?? 0)}
              </p>
            ) : (
              <p className="text-2xl md:text-4xl">Loading...</p>
            )}
          </div>
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Watched minutes(all time)</h3>
            {liveAppStreamCountsRetrieved ? (
              <p className="text-2xl md:text-4xl">
                {formatNumber(liveAppViewedMinutesTotal ?? 0)}
              </p>
            ) : (
              <p className="text-2xl md:text-4xl">Loading...</p>
            )}
          </div>
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Watched minutes (7 days)</h3>
            {liveAppStreamCountsRetrieved ? (
              <p className="text-2xl md:text-4xl">
                {formatNumber(liveAppViewedMinutesSevenDays ?? 0)}
              </p>
            ) : (
              <p className="text-2xl md:text-4xl">Loading...</p>
            )}
          </div>
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Watched minutes (30 days)</h3>
            {liveAppStreamCountsRetrieved ? (
              <p className="text-2xl md:text-4xl">
                {formatNumber(liveAppViewedMinutesThirtyDays ?? 0)}
              </p>
            ) : (
              <p className="text-2xl md:text-4xl">Loading...</p>
            )}
          </div>
        </div>
      </section>
      <section className="grid grid-cols-1 gap-4 pt-4">
        <h2 className="text-2xl">
          <Link
            title="Rankings"
            to="/analytics/titles"
            className="underline bold"
          >
            Rankings - Last 7 days <span className="text-sm"></span>
            </Link>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Top titles (by minutes streamed)</h3>
            <table className="table table-auto w-full">
                <thead>
                    <tr>
                        <th className="px-4 py-2 font-normal">Title</th>
                        <th className="px-4 py-2 font-normal">Minutes Streamed</th>
                    </tr>
                </thead>
                <tbody>
                    {liveAppRankings && liveAppRankings.items.map((item) => (
                        <tr key={item.keyId} className="border-t border-cyan-900 hover:bg-cyan-300/25 cursor-pointer" onClick={(e) => {
                                e.preventDefault();
                                if (item.keyId != "NoTitle") {
                                  navigate(`/analytics/titles/?titleId=${item.keyId}`);
                                }
                            }}>
                            <td className="px-4 py-2">{item.keyName}</td>
                            <td className="px-4 py-2">{formatNumber(item.value, 0)}</td>
                        </tr>
                        
                    ))}
                    {!liveAppRankings && (
                        <>
                         {Array.from({ length: 10 }).map((_, i) => (
                            <tr key={i}>
                                <td className="px-4 py-2 text-center">
                                    <Ghost />
                                </td>
                                <td className="px-4 py-2 text-center">
                                    <Ghost />
                                </td>
                            </tr>
                        ))}
                        </>
                    )}
                </tbody>
            </table>
          </div>
          <div className="card p-4">
            <h3 className="text-lg md:text-xl">Top streamers (by minutes streamed)</h3>
            <ul className="md:hidden list-disc list-inside mb-4">
              {liveUserRankings && liveUserRankings.items.map((item) => (
                <li key={item.keyId}>
                  <a href={`/admin/users/${item.keyId}`} onClick={(e) => {
                      e.preventDefault();
                      navigate(`/admin/users/${item.keyId}`);
                  }}><UserNameById userId={item.keyId} /></a> - <a href={`/admin/sessions/liveapp?titleId=&userId=${item.keyId}&page=1&orderBy=started&orderDirection=desc`}>{formatNumber(item.value, 0)}</a>
                </li>
              ))}
              {!liveUserRankings && (
                <>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <li key={i}>
                      <Ghost />
                    </li>
                  ))}
                </>
              )}
            </ul>
            <table className="hidden md:table table-auto w-full">
                <thead>
                    <tr>
                        <th className="px-4 py-2 font-normal">User</th>
                        <th className="px-4 py-2 font-normal">Minutes Streamed</th>
                    </tr>
                </thead>
                <tbody>
                    {liveUserRankings && liveUserRankings.items.map((item) => (
                        <tr key={item.keyId} className="border-t border-cyan-900 hover:bg-cyan-300/25 cursor-pointer" onClick={(e) => {
                                e.preventDefault();
                                navigate(`/admin/users/${item.keyId}`);
                            }}>
                            <td className="px-4 py-2"><UserNameById userId={item.keyId} /></td>
                            <td className="px-4 py-2">{formatNumber(item.value, 0)}</td>
                        </tr>
                    ))}
                    {!liveUserRankings && (
                        <>
                         {Array.from({ length: 10 }).map((_, i) => (
                            <tr key={i}>
                                <td className="px-4 py-2 text-center">
                                    <Ghost />
                                </td>
                                <td className="px-4 py-2 text-center">
                                    <Ghost />
                                </td>
                            </tr>
                        ))}
                        </>
                    )}
                </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
